/**
 * Desktop header search panel: open/close, backdrop, autofocus.
 * Mobile uses drawer-component via data-drawer-open="search-drawer".
 */
(() => {
  const DESKTOP_QUERY = '(min-width: 1024px)';
  const TRANSITION_MS = 300;

  class HeaderSearch {
    constructor() {
      this.trigger = document.querySelector('.header__icons-search-toggle');
      this.header = document.querySelector('.header');
      this.panel = document.getElementById('header-search-panel');
      this.backdrop = document.querySelector('[data-header-search-backdrop]');
      this.isOpen = false;
      this.isClosing = false;

      if (!this.trigger || !this.header || !this.panel) return;

      this.predictiveSearch = this.panel.querySelector('predictive-search');
      this.inner = this.panel.querySelector('.header-search__inner');
      this.mediaQuery = window.matchMedia(DESKTOP_QUERY);

      this.onTriggerClick = this.onTriggerClick.bind(this);
      this.onBackdropClick = this.onBackdropClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onMediaChange = this.onMediaChange.bind(this);
      this.onDrawerClassChange = this.onDrawerClassChange.bind(this);
      this.onResultsUpdated = this.onResultsUpdated.bind(this);
      this.onCloseClick = this.onCloseClick.bind(this);
      this.onInnerResize = this.onInnerResize.bind(this);
      this.onMegaMenuEnter = this.onMegaMenuEnter.bind(this);

      this.trigger.addEventListener('click', this.onTriggerClick);
      this.backdrop?.addEventListener('click', this.onBackdropClick);
      this.panel.querySelector('[data-header-search-close]')?.addEventListener('click', this.onCloseClick);
      document.addEventListener('keydown', this.onKeydown);
      this.mediaQuery.addEventListener('change', this.onMediaChange);
      this.panel.addEventListener('predictive-search:results-updated', this.onResultsUpdated);

      // Pointer only: focus must not close search (sudden context change).
      this.header.querySelectorAll('.has-megamenu').forEach((item) => {
        item.addEventListener('pointerenter', this.onMegaMenuEnter);
      });

      // Keep panel height in sync with real laid-out content (avoids scrollHeight overflow bugs)
      if (this.inner && 'ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.onInnerResize);
        this.resizeObserver.observe(this.inner);
      }

      this.searchDrawer = document.getElementById('search-drawer');
      if (this.searchDrawer) {
        this.drawerWasOpen = false;
        this.drawerObserver = new MutationObserver(this.onDrawerClassChange);
        this.drawerObserver.observe(this.searchDrawer, {
          attributes: true,
          attributeFilter: ['class'],
        });
      }
    }

    get isDesktop() {
      return this.mediaQuery.matches;
    }

    onTriggerClick(event) {
      if (!this.isDesktop) return;

      event.preventDefault();
      event.stopPropagation();

      if (this.isOpen) {
        this.close();
        return;
      }

      this.open();
    }

    onBackdropClick() {
      this.close();
    }

    onCloseClick() {
      this.close();
    }

    onMegaMenuEnter() {
      if (!this.isOpen) return;

      this.close();
    }

    onKeydown(event) {
      if (event.key !== 'Escape' || !this.isOpen) return;

      this.close();
    }

    onMediaChange() {
      if (!this.isDesktop && this.isOpen) {
        this.closeImmediate();
      }
    }

    onDrawerClassChange() {
      const isOpen = this.searchDrawer?.classList.contains('drawer--open');
      const mobileSearch = this.searchDrawer?.querySelector('predictive-search');

      if (isOpen && !this.drawerWasOpen) {
        requestAnimationFrame(() => mobileSearch?.focusInput?.());
      }

      if (!isOpen && this.drawerWasOpen) {
        mobileSearch?.clear?.();
      }

      this.drawerWasOpen = Boolean(isOpen);
    }

    onResultsUpdated() {
      if (!this.isOpen || this.isClosing) return;

      requestAnimationFrame(() => {
        requestAnimationFrame(() => this.syncPanelHeight());
      });
    }

    onInnerResize() {
      if (!this.isOpen || this.isClosing) return;
      this.syncPanelHeight();
    }

    syncPanelHeight() {
      if (!this.inner) return;

      this.panel.style.height = `${this.inner.offsetHeight}px`;
    }

    open() {
      if (this.isOpen) return;

      this.isOpen = true;
      this.isClosing = false;
      this.trigger.setAttribute('aria-expanded', 'true');
      this.panel.removeAttribute('hidden');
      this.panel.setAttribute('aria-hidden', 'false');
      this.header.classList.add('header--search-open');
      document.body.classList.add('header-search-open');

      if (this.backdrop) {
        this.backdrop.hidden = false;
        requestAnimationFrame(() => {
          this.backdrop.classList.add('header-search-backdrop--visible');
        });
      }

      this.panel.style.height = '0px';
      requestAnimationFrame(() => {
        requestAnimationFrame(() => this.syncPanelHeight());
      });

      requestAnimationFrame(() => {
        this.predictiveSearch?.focusInput?.();
      });
    }

    close() {
      if (!this.isOpen || this.isClosing) return;

      this.isClosing = true;
      this.trigger.setAttribute('aria-expanded', 'false');
      this.panel.style.height = '0px';
      this.backdrop?.classList.remove('header-search-backdrop--visible');

      const finish = () => {
        if (!this.isClosing) return;
        this.finishClose();
      };

      this.panel.addEventListener('transitionend', finish, { once: true });
      setTimeout(finish, TRANSITION_MS + 50);
    }

    closeImmediate() {
      this.isClosing = false;
      this.panel.style.height = '0px';
      this.backdrop?.classList.remove('header-search-backdrop--visible');
      this.finishClose();
    }

    finishClose() {
      this.isOpen = false;
      this.isClosing = false;
      this.panel.setAttribute('hidden', '');
      this.panel.setAttribute('aria-hidden', 'true');
      this.header.classList.remove('header--search-open');
      document.body.classList.remove('header-search-open');

      if (this.backdrop) {
        this.backdrop.hidden = true;
      }

      this.predictiveSearch?.clear?.();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => new HeaderSearch());
  } else {
    new HeaderSearch();
  }
})();
